/**
 * Welke tekst van een melding of aankondiging deze bewoner ziet
 * (specs/20261016-taal-bewonersapp, T033, T035b; FR-17, FR-18, FR-19).
 *
 *   tekst = tekst[bewoner] → tekst[site] → tekst['nl'] → eerste ingevulde (nl, fr, en, de)
 *
 * De keten zelf staat in `@caro/shared/utils` (`pickLocalizedText`), dezelfde als de API.
 * Alle talen reizen in de payload mee, dus een taalwissel toont een melding die al op het
 * scherm staat meteen in de nieuwe taal, zonder nieuw verzoek (wens 4).
 *
 * Puur, zonder React of store: de store (voorlezen) en de componenten (tonen) gebruiken
 * dezelfde functie, zodat wat er gezegd wordt altijd is wat er staat.
 */
import { isAppLanguage, pickLocalizedText } from '@caro/shared/utils';
import type {
  ResidentNotificationPayload,
  ResidentNotificationTranslation,
} from '@caro/shared/types';
import { translations, tf } from '../i18n';
import type { Language } from '../i18n';

export interface LocalizedNotificationText {
  title: string;
  body?: string;
  /** De voorleestekst in de gekozen taal, als die er expliciet is. */
  ttsText?: string;
  /** Labels per actie-`id` in de gekozen taal; leeg = de labels uit de payload. */
  actionLabels: Record<string, string>;
  /** In welke taal deze tekst staat — en dus de voorleestaal bij `tts.lang: 'auto'`. */
  lang: Language;
}

/**
 * Systeemmelding (FR-19): de sleutel opzoeken in het woordenboek en `{param}` invullen.
 * `null` bij een onbekende sleutel, zodat de aanroeper terugvalt op `title`/`body`.
 */
export function resolveSystemMessage(
  payload: Pick<ResidentNotificationPayload, 'messageKey' | 'params'>,
  lang: Language,
): LocalizedNotificationText | null {
  const key = payload.messageKey;
  if (!key) return null;
  const system = translations[lang]?.notifications?.system as
    | Record<string, { title: string; body: string } | undefined>
    | undefined;
  const entry = system && Object.prototype.hasOwnProperty.call(system, key) ? system[key] : undefined;
  if (!entry || !entry.title) return null;
  const params = payload.params && typeof payload.params === 'object' ? payload.params : {};
  return {
    title: tf(entry.title, params),
    body: entry.body ? tf(entry.body, params) : undefined,
    actionLabels: {},
    lang,
  };
}

/** De tekst van een melding in de taal van deze bewoner. */
export function localizeNotification(
  payload: ResidentNotificationPayload,
  lang: Language,
): LocalizedNotificationText {
  const system = resolveSystemMessage(payload, lang);
  if (system) return system;

  const base: Partial<ResidentNotificationTranslation> & { title: string; lang: Language } = {
    title: payload.title,
    body: payload.body,
    ttsText: payload.tts?.text,
    lang: isAppLanguage(payload.baseLanguage) ? payload.baseLanguage : 'nl',
  };
  const picked = pickLocalizedText(base, payload.translations as never, {
    lang,
    siteLanguage: isAppLanguage(payload.siteLanguage) ? payload.siteLanguage : null,
  });
  const text = picked.text as Partial<ResidentNotificationTranslation>;
  const labels = text.actionLabels && typeof text.actionLabels === 'object' ? text.actionLabels : {};
  return {
    title: text.title || payload.title,
    body: text.body,
    ttsText: text.ttsText,
    actionLabels: { ...labels },
    lang: picked.lang,
  };
}

/** Wat er voorgelezen wordt: de eigen voorleestekst, anders "titel. tekst" in die taal. */
export function localizedSpeechText(t: LocalizedNotificationText): string {
  if (t.ttsText && t.ttsText.trim()) return t.ttsText;
  return [t.title, t.body].filter((s) => s && String(s).trim()).join('. ');
}

/** Aankondiging met optionele vertalingen (T031c); dezelfde keten. */
export interface LocalizableAnnouncement {
  title: string;
  body?: string;
  baseLanguage?: string | null;
  siteLanguage?: string | null;
  translations?: Partial<Record<string, { title?: string; body?: string }>> | null;
}

export function localizeAnnouncement(
  a: LocalizableAnnouncement,
  lang: Language,
): { title: string; body?: string; lang: Language } {
  const picked = pickLocalizedText(
    { title: a.title, body: a.body, lang: isAppLanguage(a.baseLanguage) ? a.baseLanguage : 'nl' },
    a.translations as never,
    { lang, siteLanguage: isAppLanguage(a.siteLanguage) ? a.siteLanguage : null },
  );
  const text = picked.text as { title?: string; body?: string };
  return { title: text.title || a.title, body: text.body, lang: picked.lang };
}
